import React, { useState, useEffect } from 'react';
import type { Supplier } from '../../types';

interface SupplierModalProps {
  supplier: Supplier | null;
  onClose: () => void;
  onSave: (supplier: Supplier) => void;
}

const emptyForm = {
  name: '',
  contactPerson: '',
  phone: '',
  email: '',
  address: '',
  gstNumber: '',
  nzbn: '',
  paymentTerms: '',
  accountNumber: '',
};

const SupplierModal: React.FC<SupplierModalProps> = ({ supplier, onClose, onSave }) => {
  const [formData, setFormData] = useState(emptyForm);
  const [showBilling, setShowBilling] = useState(false);
  const [id, setId] = useState<number | null>(null);

  useEffect(() => {
    if (supplier) {
      setFormData({
        name: supplier.name,
        contactPerson: supplier.contactPerson,
        phone: supplier.phone,
        email: supplier.email,
        address: supplier.address ?? '',
        gstNumber: supplier.gstNumber ?? '',
        nzbn: supplier.nzbn ?? '',
        paymentTerms: supplier.paymentTerms ?? '',
        accountNumber: supplier.accountNumber ?? '',
      });
      setId(supplier.id);
      setShowBilling(
        Boolean(
          supplier.address ||
            supplier.gstNumber ||
            supplier.nzbn ||
            supplier.paymentTerms ||
            supplier.accountNumber,
        ),
      );
    } else {
      setFormData(emptyForm);
      setId(null);
      setShowBilling(false);
    }
  }, [supplier]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name.trim()) {
      alert('Supplier name is required.');
      return;
    }
    const payload: Supplier = {
      id: id || 0,
      name: formData.name.trim(),
      contactPerson: formData.contactPerson.trim(),
      phone: formData.phone.trim(),
      email: formData.email.trim(),
    };
    if (formData.address.trim()) payload.address = formData.address.trim();
    if (formData.gstNumber.trim()) payload.gstNumber = formData.gstNumber.trim();
    if (formData.nzbn.trim()) payload.nzbn = formData.nzbn.trim();
    if (formData.paymentTerms.trim()) payload.paymentTerms = formData.paymentTerms.trim();
    if (formData.accountNumber.trim()) payload.accountNumber = formData.accountNumber.trim();
    onSave(payload);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-lg bg-white p-8 shadow-xl">
        <h2 className="mb-6 text-2xl font-bold text-gray-800">
          {supplier ? 'Edit Supplier' : 'Add New Supplier'}
        </h2>
        <form onSubmit={handleSubmit}>
          <div className="mb-4">
            <label htmlFor="name" className="mb-1 block text-sm font-medium text-gray-700">
              Supplier Name
            </label>
            <input
              type="text"
              name="name"
              id="name"
              value={formData.name}
              onChange={handleChange}
              className="w-full rounded-md border border-gray-300 bg-gray-100 px-3 py-2 text-gray-900 shadow-sm focus:border-pink-500 focus:outline-none focus:ring-pink-500"
              required
            />
          </div>
          <div className="mb-4">
            <label htmlFor="contactPerson" className="mb-1 block text-sm font-medium text-gray-700">
              Contact Person
            </label>
            <input
              type="text"
              name="contactPerson"
              id="contactPerson"
              value={formData.contactPerson}
              onChange={handleChange}
              className="w-full rounded-md border border-gray-300 bg-gray-100 px-3 py-2 text-gray-900 shadow-sm focus:border-pink-500 focus:outline-none focus:ring-pink-500"
            />
          </div>
          <div className="mb-4">
            <label htmlFor="phone" className="mb-1 block text-sm font-medium text-gray-700">
              Phone
            </label>
            <input
              type="tel"
              name="phone"
              id="phone"
              value={formData.phone}
              onChange={handleChange}
              className="w-full rounded-md border border-gray-300 bg-gray-100 px-3 py-2 text-gray-900 shadow-sm focus:border-pink-500 focus:outline-none focus:ring-pink-500"
            />
          </div>
          <div className="mb-4">
            <label htmlFor="email" className="mb-1 block text-sm font-medium text-gray-700">
              Email
            </label>
            <input
              type="email"
              name="email"
              id="email"
              value={formData.email}
              onChange={handleChange}
              className="w-full rounded-md border border-gray-300 bg-gray-100 px-3 py-2 text-gray-900 shadow-sm focus:border-pink-500 focus:outline-none focus:ring-pink-500"
            />
          </div>

          <button
            type="button"
            onClick={() => setShowBilling((v) => !v)}
            className="mb-3 text-sm font-medium text-pink-600 hover:text-pink-800"
          >
            {showBilling ? '− Hide billing / invoice details' : '+ Billing / invoice details'}
          </button>

          {showBilling ? (
            <div className="mb-4 space-y-3 rounded-lg border border-gray-200 bg-gray-50 p-4">
              <div>
                <label htmlFor="address" className="mb-1 block text-sm font-medium text-gray-700">
                  Address
                </label>
                <textarea
                  name="address"
                  id="address"
                  rows={2}
                  value={formData.address}
                  onChange={handleChange}
                  className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                  placeholder="Supplier billing address"
                />
              </div>
              <div>
                <label htmlFor="gstNumber" className="mb-1 block text-sm font-medium text-gray-700">
                  GST Number
                </label>
                <input
                  type="text"
                  name="gstNumber"
                  id="gstNumber"
                  value={formData.gstNumber}
                  onChange={handleChange}
                  placeholder="e.g. 123-456-789"
                  className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                />
              </div>
              <div>
                <label htmlFor="nzbn" className="mb-1 block text-sm font-medium text-gray-700">
                  NZBN
                </label>
                <input
                  type="text"
                  name="nzbn"
                  id="nzbn"
                  value={formData.nzbn}
                  onChange={handleChange}
                  className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                />
              </div>
              <div>
                <label
                  htmlFor="paymentTerms"
                  className="mb-1 block text-sm font-medium text-gray-700"
                >
                  Payment terms
                </label>
                <input
                  type="text"
                  name="paymentTerms"
                  id="paymentTerms"
                  value={formData.paymentTerms}
                  onChange={handleChange}
                  placeholder="e.g. 20th of month following"
                  className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                />
              </div>
              <div>
                <label
                  htmlFor="accountNumber"
                  className="mb-1 block text-sm font-medium text-gray-700"
                >
                  Your account number
                </label>
                <input
                  type="text"
                  name="accountNumber"
                  id="accountNumber"
                  value={formData.accountNumber}
                  onChange={handleChange}
                  className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                />
              </div>
            </div>
          ) : null}

          <div className="flex justify-end space-x-4">
            <button
              type="button"
              onClick={onClose}
              className="rounded-md bg-gray-200 px-4 py-2 text-gray-800 hover:bg-gray-300"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="rounded-md bg-pink-500 px-4 py-2 text-white hover:bg-pink-600"
            >
              Save Supplier
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default SupplierModal;
